import type { TargetProbeLegMember, TargetProbeLegRecord } from '@nadoutong/contracts';
import type { I18nKey, TParams } from '../../i18n/index.ts';
import type { LampTone } from '../../ui/StatusLamp.tsx';
import { legEvidence, triggerKey } from '../guaranteed/legEvidence.ts';
import {
  immunityMinutesRemaining,
  legLamp,
  legLampTone,
  minutesBefore,
  type LegLamp,
  type LegSkuArming,
} from '../guaranteed/legModel.ts';

type Translate = (key: I18nKey, params?: TParams) => string;

/**
 * The region ops reads routing state for. Since 0081 (#2604) `/legs` returns
 * one leg per region for the same member target ids (CN rows are uplinked
 * mirrors); rows without a region predate 0081 and count as the serving one.
 */
export const OPS_SERVING_REGION = 'jp';

/** Physical-leg state behind one routing target, split by region. */
export interface TargetLegs {
  /** The serving region's leg; null when only other regions report one. */
  readonly serving: TargetProbeLegRecord | null;
  /** This target's own member row (serving region first); carries its enabled flag. */
  readonly member: TargetProbeLegMember | null;
  /** The same target's legs in other regions. */
  readonly others: readonly TargetProbeLegRecord[];
}

/** The guaranteed-center lamp, plus a tripped row on an SKU with no act-mode mechanism. */
export type TargetLamp = LegLamp | 'tripped_unarmed';

function regionOf(leg: TargetProbeLegRecord): string {
  const region: string | undefined = leg.region;
  return region === undefined || region === '' ? OPS_SERVING_REGION : region;
}

export function indexLegsByTargetId(
  legs: readonly TargetProbeLegRecord[],
): ReadonlyMap<string, TargetLegs> {
  const serving = new Map<string, TargetProbeLegRecord>();
  const others = new Map<string, readonly TargetProbeLegRecord[]>();
  const members = new Map<string, TargetProbeLegMember>();
  for (const leg of legs) {
    const isServing = regionOf(leg) === OPS_SERVING_REGION;
    for (const member of leg.members) {
      if (isServing) serving.set(member.target_id, leg);
      else others.set(member.target_id, [...(others.get(member.target_id) ?? []), leg]);
      if (isServing || !members.has(member.target_id)) members.set(member.target_id, member);
    }
  }
  const ids = new Set([...serving.keys(), ...others.keys()]);
  return new Map([...ids].map((id) => [id, {
    serving: serving.get(id) ?? null,
    member: members.get(id) ?? null,
    others: others.get(id) ?? [],
  }]));
}

/**
 * A disabled target is out of routing whatever its leg says, so `manual`
 * wins over a trip record — the same precedence as the guaranteed page.
 */
export function targetLamp(
  leg: TargetProbeLegRecord | null,
  arming: LegSkuArming,
  nowMs: number,
  member: TargetProbeLegMember | null = null,
): TargetLamp {
  if (member !== null && !member.enabled) return 'manual';
  if (leg === null) return 'unprobed';
  const lamp = legLamp(leg, nowMs);
  return lamp === 'tripped' && !arming.armed ? 'tripped_unarmed' : lamp;
}

/** Red only for a removal that actually moves routing; a record with mechanisms off is grey. */
export function targetLampTone(lamp: TargetLamp): LampTone {
  return lamp === 'tripped_unarmed' ? 'off' : legLampTone(lamp);
}

/** Scannable chip beside the dot: only for a live removal and for a shadow verdict. */
export function targetLampChip(lamp: TargetLamp, leg: TargetProbeLegRecord | null): I18nKey | null {
  if (lamp === 'shadow') return 'guaranteed.leg.chip.shadow';
  if (lamp !== 'tripped' || leg === null) return null;
  switch (leg.trip_reason) {
    case 'chain':
      return 'tenant_routing.leg.chip.tripped_chain';
    case 'jev':
      return 'tenant_routing.leg.chip.tripped_jev';
    case 'probe':
    case null:
      return 'tenant_routing.leg.chip.tripped_probe';
  }
}

function headline(lamp: TargetLamp, leg: TargetProbeLegRecord, nowMs: number, t: Translate): string {
  switch (lamp) {
    case 'ok':
      return t('tenant_routing.leg.tip.ok');
    case 'failing':
      return t('tenant_routing.leg.tip.failing');
    case 'shadow':
      return t('tenant_routing.leg.tip.shadow');
    case 'tripped':
      return t('tenant_routing.leg.tip.tripped');
    case 'tripped_unarmed':
      return t('tenant_routing.leg.tip.tripped_unarmed');
    case 'immune':
      return t('tenant_routing.leg.tip.immune', {
        minutes: String(immunityMinutesRemaining(leg.immune_until, nowMs) ?? 0),
      });
    case 'manual':
      return t('tenant_routing.leg.tip.manual');
    case 'unprobed':
      return t('tenant_routing.leg.tip.unprobed');
  }
}

function otherRegionKey(lamp: TargetLamp): I18nKey | null {
  switch (lamp) {
    case 'tripped':
      return 'tenant_routing.leg.other_region.tripped';
    case 'tripped_unarmed':
      return 'tenant_routing.leg.other_region.tripped_unarmed';
    case 'shadow':
      return 'tenant_routing.leg.other_region.shadow';
    default:
      return null;
  }
}

/** Leg records carry no trip time, so a removal is dated by its last probe, and says so. */
function otherRegionWhen(leg: TargetProbeLegRecord, shadow: boolean, nowMs: number, t: Translate): string {
  const judged = shadow ? leg.shadow_tripped_at : null;
  const at = judged ?? leg.last_probe_at;
  const agoMin = at === null ? null : minutesBefore(at, nowMs);
  if (agoMin === null) return '';
  const ago = t('guaranteed.leg.minutes_ago', { minutes: String(agoMin) });
  return judged !== null
    ? t('tenant_routing.leg.other_region.judged', { ago })
    : t('tenant_routing.leg.other_region.last_probe', { ago });
}

function otherRegionLine(
  leg: TargetProbeLegRecord,
  servingLamp: TargetLamp,
  arming: LegSkuArming,
  nowMs: number,
  t: Translate,
): string | null {
  const lamp = targetLamp(leg, arming, nowMs);
  const key = otherRegionKey(lamp);
  if (key === null || lamp === servingLamp) return null;
  const shadow = lamp === 'shadow';
  const trigger = t(triggerKey(shadow ? leg.shadow_trip_reason : leg.trip_reason));
  const state = t(key, { region: regionOf(leg).toUpperCase(), trigger });
  const when = otherRegionWhen(leg, shadow, nowMs, t);
  return when.length > 0 ? t('tenant_routing.leg.other_region.with_when', { state, when }) : state;
}

/** A disabled target may still hold a trip or shadow record; name it as secondary. */
function manualRecordLine(leg: TargetProbeLegRecord | null, nowMs: number, t: Translate): string {
  if (leg === null) return '';
  if (leg.tripped) {
    return t('tenant_routing.leg.tip.manual_also_tripped', {
      evidence: legEvidence(leg, 'tripped', nowMs, t, undefined, { compact: true }),
    });
  }
  if (leg.shadow_trip_reason !== null) {
    return t('tenant_routing.leg.tip.manual_also_shadow', {
      evidence: legEvidence(leg, 'shadow', nowMs, t, undefined, { compact: true }),
    });
  }
  return '';
}

function ownLines(
  legs: TargetLegs | null,
  lamp: TargetLamp,
  arming: LegSkuArming,
  nowMs: number,
  t: Translate,
): readonly string[] {
  const serving = legs?.serving ?? null;
  if (lamp === 'manual') return [t('tenant_routing.leg.tip.manual'), manualRecordLine(serving, nowMs, t)];
  if (serving === null) return [t('tenant_routing.probe.dot_never')];
  if (lamp === 'unprobed') return [headline(lamp, serving, nowMs, t)];
  const evidenceLamp = lamp === 'tripped_unarmed' ? 'tripped' : lamp;
  return [
    headline(lamp, serving, nowMs, t),
    legEvidence(serving, evidenceLamp, nowMs, t, arming, { compact: true }),
  ];
}

/**
 * Hover text: the state in plain words and what it does to routing, then the
 * guaranteed-center evidence line, then any other region that disagrees.
 */
export function targetLampTip(
  legs: TargetLegs | null,
  arming: LegSkuArming,
  nowMs: number,
  t: Translate,
): readonly string[] {
  const lamp = targetLamp(legs?.serving ?? null, arming, nowMs, legs?.member ?? null);
  // A disabled target is out of routing in every region; other regions' records add nothing.
  const others = lamp === 'manual'
    ? []
    : (legs?.others ?? []).map((leg) => otherRegionLine(leg, lamp, arming, nowMs, t));
  return [...ownLines(legs, lamp, arming, nowMs, t), ...others]
    .filter((line): line is string => line !== null && line.length > 0);
}
